import React from 'react';
import { Loader } from '../../elements';
import { AnimalTrendDataType, AnimalType } from '../../models/animal';
import { extractApiErrorMessage, notifications } from '../../utils';
import { LoaderSize } from '../../constants/constants';
import { useTranslation } from 'react-i18next';
import { ApiService } from '../../hooks';
import {
  AnimalTrendDataPoint,
  BirthCycleOption,
  toBirthCycleOption,
  toAnimalTrendData
} from '../../models/animal-trend-data';
import { AnimalSlaughterhouseData, AnimalWeight } from '../../models/animal-view';
import TrendData from '../TrendData';

export interface IProps {
  animalId: string;
  animalType: AnimalType;
  /**
   * Owned by the parent so the user's selection survives the remounts caused by tab switches and
   * animal reloads - holding it here reset it to the initial type and silently refetched that one.
   */
  animalTrendDataType: AnimalTrendDataType;
  onAnimalTrendDataTypeChanged: (animalTrendDataType: AnimalTrendDataType) => void;
}

export default function AnimalTrendData({
  animalId,
  animalType,
  animalTrendDataType,
  onAnimalTrendDataTypeChanged
}: IProps) {
  const { t } = useTranslation();

  const apiService = new ApiService();

  const [isBusy, setIsBusy] = React.useState(false);
  const [trendData, setTrendData] = React.useState<AnimalTrendDataPoint[]>([]);
  const [slaughterhouseDataAverage, setSlaughterhouseDataAverage] = React.useState<AnimalSlaughterhouseData>();
  const [weightAverage, setWeightAverage] = React.useState<AnimalWeight>();
  const [reloadToken, setReloadToken] = React.useState(0);

  const [birthCycles, setBirthCycles] = React.useState<BirthCycleOption[]>([]);
  const [selectedCycleDateOfBirth, setSelectedCycleDateOfBirth] = React.useState<string | null>(null);
  const [isLoadingCycles, setIsLoadingCycles] = React.useState(false);

  // Refetch whenever a request input changes. The previous `loaded` flag was a no-op when it was
  // already false, so changes made while a request was in flight were silently dropped, and a
  // failed request left the flag false forever - hiding the whole section until a page reload.
  React.useEffect(() => {
    let cancelled = false;

    setIsBusy(true);

    apiService.api.animals
      .animalsGetAnimalTrendData({
        AnimalId: animalId,
        AnimalType: animalType,
        AnimalTrendDataType: animalTrendDataType,
        DateOfBirth: selectedCycleDateOfBirth ?? undefined
      })
      .then((result) => {
        if (cancelled) {
          return;
        }
        const animalTrendData = toAnimalTrendData(result.data);
        setTrendData(animalTrendData.trendData);
        setSlaughterhouseDataAverage(animalTrendData.slaughterhouseDataAverage ?? undefined);
        setWeightAverage(animalTrendData.weightAverage ?? undefined);
      })
      .catch((e) => {
        if (cancelled) {
          return;
        }
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        if (!cancelled) {
          setIsBusy(false);
        }
      });

    // Ignore a superseded response so a slow earlier request cannot overwrite the current one.
    return () => {
      cancelled = true;
    };
  }, [animalId, animalType, animalTrendDataType, selectedCycleDateOfBirth, reloadToken]);

  const loadBirthCycles = async () => {
    setIsLoadingCycles(true);
    apiService.api.animals
      .animalsGetAnimalPigletsBirthCycles({ AnimalId: animalId, AnimalType: animalType })
      .then((result) => {
        setBirthCycles(result.data.data.map(toBirthCycleOption));
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => setIsLoadingCycles(false));
  };

  React.useEffect(() => {
    loadBirthCycles();
  }, [animalId, animalType]);

  return (
    <>
      <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />

      <TrendData
        animalTrendDataType={animalTrendDataType}
        trendData={trendData}
        slaughterhouseDataAverage={slaughterhouseDataAverage}
        weightAverage={weightAverage}
        onAnimalTrendDataTypeChanged={onAnimalTrendDataTypeChanged}
        reloadTrendData={() => setReloadToken((token) => token + 1)}
        birthCycles={birthCycles}
        selectedCycleDateOfBirth={selectedCycleDateOfBirth}
        onCycleChanged={setSelectedCycleDateOfBirth}
        isLoadingCycles={isLoadingCycles}
      />
    </>
  );
}
